import { BarChart3, BookOpen, KeyRound, LockKeyhole, Map, MessageSquareText, Monitor, SearchCheck, Sparkles } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from "react";
import { BASICS_CURRICULUM_ID, isBasicsCurriculumMap, isKanaCurriculumMap } from "../shared/curriculum";
import { kanaWordLesson } from "../shared/kana-words";
import { EXPRESSIONS_CURRICULUM_ID } from "../shared/expressions";
import type { Course, CurriculumLesson, CurriculumMap, Lesson, LessonProgress, User, LearningData } from "../shared/types";
import type { JapaneseReadingSection } from "./reading/ReadingView";
import { ApiClient } from "./shared/api";
import { SubjectSwitch } from "./shared/SubjectSwitch";
import { KOREAN_TRANSLATION_STORAGE_KEY, readKoreanTranslationEnabled, SentenceTranslationProvider } from "./shared/SentenceTranslation";

const ReadingView = lazy(() => import("./reading/ReadingView").then(module => ({ default: module.ReadingView })));
const DescribingView = lazy(() => import("./describing/DescribingView").then(module => ({ default: module.DescribingView })));
const DialogueView = lazy(() => import("./dialogue/DialogueView").then(module => ({ default: module.DialogueView })));
const SentenceLessonView = lazy(() => import("./shared/SentenceLessonView").then(module => ({ default: module.SentenceLessonView })));
const CurriculumAdminView = lazy(() => import("./admin/CurriculumAdminView").then(module => ({ default: module.CurriculumAdminView })));
const AiGenerationView = lazy(() => import("./admin/CurriculumAdminView").then(module => ({ default: module.AiGenerationView })));
const AnalyticsView = lazy(() => import("./admin/AnalyticsView").then(module => ({ default: module.AnalyticsView })));
const ComputerView = lazy(() => import("./computer/ComputerView").then(module => ({ default: module.ComputerView })));

type ActiveView = "computer" | "basics" | "grammar" | "reading" | "describing" | "dialogue" | "ai-generation" | "curriculum" | "analytics";
type LessonScreen = "chooser" | "material";
type AccessState = "checking" | "locked" | "unlocked";
type LearningDataState = "loading" | "ready" | "error";
const LEARNER_STORAGE_KEY = "jireh-lingo-selected-learner";
const COURSE_STORAGE_KEY = "jireh-lingo-selected-course";
const SUBJECT_STORAGE_KEY = "jireh-lingo-selected-subject";

export function mergeCurriculumMapPreservingProgress(current: CurriculumMap, incoming: CurriculumMap): CurriculumMap {
  const progressByLessonId = new globalThis.Map(
    current.lessons.map((item) => [item.lessonId, {
      completionPercent: item.completionPercent,
      completedAt: item.completedAt
    }])
  );

  return {
    ...incoming,
    lessons: incoming.lessons.map((item) => {
      const existingProgress = progressByLessonId.get(item.lessonId);
      return existingProgress ? { ...item, ...existingProgress } : item;
    })
  };
}

export function learningLanguageCourses(courses: Course[]): Course[] {
  const languageOrder: Record<string, number> = { en: 0, ja: 1 };
  return courses
    .filter((course) => course.domainType === "language")
    .sort((left, right) => (languageOrder[left.languageCode ?? ""] ?? 2) - (languageOrder[right.languageCode ?? ""] ?? 2));
}

export function App() {
  const api = useMemo(() => new ApiClient(), []);
  const [accessState, setAccessState] = useState<AccessState>("checking");
  const [pinInput, setPinInput] = useState("");
  const [pinMessage, setPinMessage] = useState("Checking access...");
  const [user, setUser] = useState<User>({ id: "", displayName: "", role: "learner" });
  const [learners, setLearners] = useState<User[]>([]);
  const [selectedLearnerId, setSelectedLearnerId] = useState(() => readStoredLearnerId());
  const [learningDataState, setLearningDataState] = useState<LearningDataState>("loading");
  const [learningDataMessage, setLearningDataMessage] = useState("Loading server progress...");
  const [courses, setCourses] = useState<Course[]>([]);
  const [activeCourseId, setActiveCourseId] = useState(() => readStoredCourseId());
  const [furiganaEnabled, setFuriganaEnabled] = useState(true);
  const [koreanTranslationEnabled, setKoreanTranslationEnabled] = useState(readKoreanTranslationEnabled);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [curriculumMaps, setCurriculumMaps] = useState<CurriculumMap[]>([]);
  const [selectedReadingCurriculumId, setSelectedReadingCurriculumId] = useState("curriculum-english-a2-reading");
  const [selectedDescribingCurriculumId, setSelectedDescribingCurriculumId] = useState("curriculum-english-a2-describing");
  const [selectedGrammarCurriculumId, setSelectedGrammarCurriculumId] = useState("curriculum-english-grammar");
  const [selectedDialogueCurriculumId, setSelectedDialogueCurriculumId] = useState("");
  const [selectedAdminCurriculumId, setSelectedAdminCurriculumId] = useState("curriculum-english-a2-reading");
  const [selectedReadingLessonId, setSelectedReadingLessonId] = useState("");
  const [selectedKanaItemId, setSelectedKanaItemId] = useState<string | null>(null);
  const [selectedDescribingLessonId, setSelectedDescribingLessonId] = useState("");
  const [selectedGrammarLessonId, setSelectedGrammarLessonId] = useState("");
  const [selectedBasicsLessonId, setSelectedBasicsLessonId] = useState("");
  const [basicsScreen, setBasicsScreen] = useState<LessonScreen>("chooser");
  const [selectedDialogueLessonId, setSelectedDialogueLessonId] = useState("");
  const [adminEditLessonId, setAdminEditLessonId] = useState<string | null>(null);
  const [activeView, setActiveView] = useState<ActiveView>(() => {
    try { return localStorage.getItem(SUBJECT_STORAGE_KEY) === "computer" ? "computer" : "reading"; }
    catch { return "reading"; }
  });
  const [japaneseReadingSection, setJapaneseReadingSection] = useState<JapaneseReadingSection>("kana");
  const [readingScreen, setReadingScreen] = useState<LessonScreen>("chooser");
  const [describingScreen, setDescribingScreen] = useState<LessonScreen>("chooser");
  const [grammarScreen, setGrammarScreen] = useState<LessonScreen>("chooser");
  const [dialogueScreen, setDialogueScreen] = useState<LessonScreen>("chooser");
  const [analytics, setAnalytics] = useState<unknown>(null);
  const hydratedReadingLessonIdsRef = useRef<Set<string>>(new Set());
  const hydratedDescribingLessonIdsRef = useRef<Set<string>>(new Set());
  const hydratedGrammarLessonIdsRef = useRef<Set<string>>(new Set());
  const hydratedDialogueLessonIdsRef = useRef<Set<string>>(new Set());
  const learningLoadIdRef = useRef(0);
  const uiClickAudioContextRef = useRef<AudioContext | null>(null);
  const playUiClick = useCallback(() => playUiClickSound(uiClickAudioContextRef), []);

  const activeCourse = courses.find((course) => course.id === activeCourseId) ?? courses[0] ?? null;
  const activeCourseMaps = curriculumMaps.filter((map) => map.curriculum.courseId === activeCourse?.id);
  const kanaMaps = activeCourseMaps.filter(isKanaCurriculumMap);
  const readingMaps = activeCourseMaps.filter((map) => map.curriculum.moduleType === "reading" && !isKanaCurriculumMap(map) && !isBasicsCurriculumMap(map));
  const describingMaps = activeCourseMaps.filter((map) => map.curriculum.moduleType === "describing");
  const basicsMap = activeCourseMaps.find(isBasicsCurriculumMap) ?? null;
  const basicsLessonIds = new Set(basicsMap?.lessons.map(item => item.lessonId) ?? []);
  const selectedBasicsLesson = basicsMap?.lessons.find(item => item.lessonId === selectedBasicsLessonId)?.lesson ?? null;
  const grammarMaps = activeCourseMaps.filter((map) => map.curriculum.moduleType === "grammar");
  const dialogueMaps = activeCourseMaps.filter((map) => map.curriculum.moduleType === "dialogue");
  const selectedStandardReadingCurriculumMap = readingMaps.find((map) => map.curriculum.id === selectedReadingCurriculumId) ?? readingMaps[0] ?? null;
  const selectedKanaCurriculumMap = kanaMaps[0] ?? null;
  const selectedDescribingCurriculumMap = describingMaps.find((map) => map.curriculum.id === selectedDescribingCurriculumId) ?? describingMaps[0] ?? null;
  const selectedGrammarCurriculumMap = grammarMaps.find((map) => map.curriculum.id === selectedGrammarCurriculumId) ?? grammarMaps[0] ?? null;
  const selectedDialogueCurriculumMap = dialogueMaps.find((map) => map.curriculum.id === selectedDialogueCurriculumId) ?? dialogueMaps[0] ?? null;
  const selectedAdminCurriculumMap = activeCourseMaps.find((map) => map.curriculum.id === selectedAdminCurriculumId) ?? activeCourseMaps[0] ?? null;
  const readingLessons = lessons.filter((lesson) => lesson.courseId === activeCourse?.id && lesson.moduleType === "reading" && lesson.lessonKind !== "kana" && !basicsLessonIds.has(lesson.id));
  const kanaLessons = lessons.filter((lesson) => lesson.courseId === activeCourse?.id && (lesson.lessonKind === "kana" || kanaWordLesson(lesson.id)));
  const describingLessons = lessons.filter((lesson) => lesson.courseId === activeCourse?.id && lesson.moduleType === "describing");
  const grammarLessons = lessons.filter((lesson) => lesson.courseId === activeCourse?.id && lesson.moduleType === "grammar");
  const dialogueLessons = lessons.filter((lesson) => lesson.courseId === activeCourse?.id && lesson.moduleType === "dialogue");
  const selectedStandardReadingLesson = readingLessons.find((lesson) => lesson.id === selectedReadingLessonId) ?? selectedStandardReadingCurriculumMap?.lessons[0]?.lesson ?? readingLessons[0] ?? null;
  const selectedKanaLesson = kanaLessons.find((lesson) => lesson.id === selectedReadingLessonId) ?? selectedKanaCurriculumMap?.lessons[0]?.lesson ?? kanaLessons[0] ?? null;
  const selectedDescribingLesson = describingLessons.find((lesson) => lesson.id === selectedDescribingLessonId) ?? selectedDescribingCurriculumMap?.lessons[0]?.lesson ?? describingLessons[0] ?? null;
  const selectedGrammarLesson = grammarLessons.find((lesson) => lesson.id === selectedGrammarLessonId) ?? selectedGrammarCurriculumMap?.lessons[0]?.lesson ?? grammarLessons[0] ?? null;
  const selectedDialogueLesson = dialogueLessons.find((lesson) => lesson.id === selectedDialogueLessonId) ?? selectedDialogueCurriculumMap?.lessons[0]?.lesson ?? dialogueLessons[0] ?? null;
  const isJapaneseCourse = activeCourse?.languageCode === "ja";
  const isComputerSelected = activeView === "computer";
  const isKanaSection = isJapaneseCourse && japaneseReadingSection !== "reading" && japaneseReadingSection !== "expressions";
  const selectedReadingCurriculumMap = isKanaSection ? selectedKanaCurriculumMap : selectedStandardReadingCurriculumMap;
  const selectedReadingLesson = isKanaSection ? selectedKanaLesson : selectedStandardReadingLesson;
  const kanaNavActive = isJapaneseCourse && activeView === "reading" && isKanaSection;
  const translationLesson = activeView === "reading" && readingScreen === "material" && !isKanaSection ? selectedReadingLesson
    : activeView === "describing" && describingScreen === "material" ? selectedDescribingLesson
    : activeView === "dialogue" && dialogueScreen === "material" ? selectedDialogueLesson
    : activeView === "grammar" && grammarScreen === "material" ? selectedGrammarLesson
    : activeView === "basics" && basicsScreen === "material" ? selectedBasicsLesson : null;

  useEffect(() => {
    try { localStorage.setItem(SUBJECT_STORAGE_KEY, isComputerSelected ? "computer" : "language"); }
    catch { /* Subject switching remains usable when storage is unavailable. */ }
  }, [isComputerSelected]);

  const loadLearningData = async (learnerId: string): Promise<boolean> => {
    const loadId = ++learningLoadIdRef.current;
    setLearningDataState("loading");
    setLearningDataMessage("Loading server progress...");
    const learnerApi = new ApiClient(learnerId);
    let result: LearningData;
    try {
      result = await learnerApi.getLearningData();
    } catch (error) {
      if (loadId === learningLoadIdRef.current) {
        setLearningDataState("error");
        setLearningDataMessage(error instanceof Error ? error.message : "Could not load server progress.");
      }
      throw error;
    }
    if (loadId !== learningLoadIdRef.current) return false;

    const { user: me, courses: courseList, curriculumMaps: curriculumList, progress: progressList, unplacedLessons: standaloneLessons } = result;
    if (me.id !== learnerId) {
      setLearningDataState("error");
      setLearningDataMessage("The server returned a different learner. Refresh and try again.");
      throw new Error("Learner response did not match the selected learner.");
    }

    api.setLearnerId(learnerId);
    setUser(me);
    setSelectedLearnerId(learnerId);
    storeLearnerId(learnerId);
    const availableCourses = courseList;
    setCourses(availableCourses);
    setActiveCourseId((current) => availableCourses.some((course) => course.id === current) ? current : learningLanguageCourses(availableCourses)[0]?.id ?? availableCourses[0]?.id ?? "");
    if (curriculumList.length) {
      const nextCurriculumList = applyLessonProgressToCurriculumMaps(
        addUnplacedLessonsToCurriculumMaps(curriculumList, standaloneLessons),
        progressList
      );
      setCurriculumMaps(nextCurriculumList);
      const nextReadingMaps = nextCurriculumList.filter((map) => map.curriculum.moduleType === "reading" && !isKanaCurriculumMap(map) && !isBasicsCurriculumMap(map));
      const nextDescribingMaps = nextCurriculumList.filter((map) => map.curriculum.moduleType === "describing");
      const nextGrammarMaps = nextCurriculumList.filter((map) => map.curriculum.moduleType === "grammar");
      const nextDialogueMaps = nextCurriculumList.filter((map) => map.curriculum.moduleType === "dialogue");
      setSelectedReadingCurriculumId((current) => nextReadingMaps.some((map) => map.curriculum.id === current) ? current : nextReadingMaps[0]?.curriculum.id ?? "");
      setSelectedDescribingCurriculumId((current) => nextDescribingMaps.some((map) => map.curriculum.id === current) ? current : nextDescribingMaps[0]?.curriculum.id ?? "");
      setSelectedGrammarCurriculumId((current) => nextGrammarMaps.some((map) => map.curriculum.id === current) ? current : nextGrammarMaps[0]?.curriculum.id ?? "");
      setSelectedDialogueCurriculumId((current) => nextDialogueMaps.some((map) => map.curriculum.id === current) ? current : nextDialogueMaps[0]?.curriculum.id ?? "");
      setSelectedAdminCurriculumId((current) => nextCurriculumList.some((map) => map.curriculum.id === current) ? current : nextCurriculumList[0].curriculum.id);
      const curriculumLessons = nextCurriculumList.flatMap((map) => map.lessons.map((item) => item.lesson));
      const mergedLessons = dedupeLessons([...curriculumLessons, ...standaloneLessons]);
      setLessons(mergedLessons);
      setSelectedReadingLessonId((current) => keepSelectedLesson(current, mergedLessons, "reading"));
      setSelectedDescribingLessonId((current) => keepSelectedLesson(current, mergedLessons, "describing"));
      setSelectedGrammarLessonId((current) => keepSelectedLesson(current, mergedLessons, "grammar"));
      setSelectedDialogueLessonId((current) => keepSelectedLesson(current, mergedLessons, "dialogue"));
      setLearningDataState("ready");
      setLearningDataMessage("");
      return true;
    }
    setCurriculumMaps([]);
    setLessons(standaloneLessons);
    setSelectedReadingLessonId((current) => keepSelectedLesson(current, standaloneLessons, "reading"));
    setSelectedDescribingLessonId((current) => keepSelectedLesson(current, standaloneLessons, "describing"));
    setSelectedGrammarLessonId((current) => keepSelectedLesson(current, standaloneLessons, "grammar"));
    setSelectedDialogueLessonId((current) => keepSelectedLesson(current, standaloneLessons, "dialogue"));
    setLearningDataState("ready");
    setLearningDataMessage("");
    return true;
  };

  useEffect(() => {
    api.getPinStatus()
      .then((status) => {
        setAccessState(status.unlocked ? "unlocked" : "locked");
        setPinMessage(status.pinRequired ? "Enter the app PIN." : "Local PIN is not configured.");
      })
      .catch(() => {
        setAccessState("locked");
        setPinMessage("Enter the app PIN.");
      });
  }, [api]);

  useEffect(() => {
    async function load() {
      if (accessState !== "unlocked") return;
      try {
        const learnerList = await api.getLearners();
        const availableLearners = learnerList;
        const storedLearnerId = readStoredLearnerId();
        const nextLearner = availableLearners.find((learner) => learner.id === storedLearnerId) ?? availableLearners[0];
        setLearners(availableLearners);
        if (!nextLearner) throw new Error("No active learner is available.");
        await loadLearningData(nextLearner.id);
      } catch (error) {
        setLearningDataState("error");
        setLearningDataMessage(error instanceof Error ? error.message : "Could not load learning data.");
      }
    }
    void load();
  }, [accessState, api]);

  useEffect(() => {
    if (learningDataState !== "ready") return;
    if (!activeCourse) return;
    storeCourseId(activeCourse.id);
    const nextMaps = curriculumMaps.filter((map) => map.curriculum.courseId === activeCourse.id);
    const nextReadingMaps = nextMaps.filter((map) => map.curriculum.moduleType === "reading" && !isKanaCurriculumMap(map) && !isBasicsCurriculumMap(map));
    const nextDescribingMaps = nextMaps.filter((map) => map.curriculum.moduleType === "describing");
    const nextGrammarMaps = nextMaps.filter((map) => map.curriculum.moduleType === "grammar");
    const nextDialogueMaps = nextMaps.filter((map) => map.curriculum.moduleType === "dialogue");
    setSelectedReadingCurriculumId((current) => nextReadingMaps.some((map) => map.curriculum.id === current) ? current : nextReadingMaps[0]?.curriculum.id ?? "");
    setSelectedDescribingCurriculumId((current) => nextDescribingMaps.some((map) => map.curriculum.id === current) ? current : nextDescribingMaps[0]?.curriculum.id ?? "");
    setSelectedGrammarCurriculumId((current) => nextGrammarMaps.some((map) => map.curriculum.id === current) ? current : nextGrammarMaps[0]?.curriculum.id ?? "");
    setSelectedDialogueCurriculumId((current) => nextDialogueMaps.some((map) => map.curriculum.id === current) ? current : nextDialogueMaps[0]?.curriculum.id ?? "");
    setSelectedAdminCurriculumId((current) => nextMaps.some((map) => map.curriculum.id === current) ? current : nextMaps[0]?.curriculum.id ?? "");
    setSelectedReadingLessonId((current) => keepSelectedLessonForCourse(current, lessons, "reading", activeCourse.id));
    setSelectedDescribingLessonId((current) => keepSelectedLessonForCourse(current, lessons, "describing", activeCourse.id));
    setSelectedGrammarLessonId((current) => keepSelectedLessonForCourse(current, lessons, "grammar", activeCourse.id));
    setSelectedDialogueLessonId((current) => keepSelectedLessonForCourse(current, lessons, "dialogue", activeCourse.id));
  }, [activeCourse, curriculumMaps, learningDataState, lessons]);

  useEffect(() => {
    if (accessState !== "unlocked" || learningDataState !== "ready" || !activeCourse || activeCourse.languageCode !== "ja") return;
    api.getLearningPreference(activeCourse.id)
      .then((preference) => setFuriganaEnabled(preference.furiganaEnabled))
      .catch((error) => console.warn(error instanceof Error ? error.message : "Could not load Japanese display preference."));
  }, [accessState, activeCourse, api, learningDataState, selectedLearnerId]);

  useEffect(() => {
    if (activeView !== "analytics" || accessState !== "unlocked") return;
    api.getAnalytics().then(setAnalytics).catch(() => setAnalytics(null));
  }, [accessState, activeView, api]);

  const unlockWithPin = async () => {
    if (!pinInput.trim()) {
      setPinMessage("PIN is required.");
      return;
    }
    try {
      setPinMessage("Checking PIN...");
      await api.verifyPin(pinInput);
      setAccessState("unlocked");
      setPinInput("");
      setPinMessage("");
    } catch {
      setAccessState("locked");
      setPinMessage("PIN is incorrect.");
    }
  };

  const handleLessonCreated = (
    lesson: Lesson,
    placement?: { curriculumId: string; categoryTitle: string; sortOrder: number },
    options: { openReading?: boolean; openDescribing?: boolean } = {}
  ) => {
    setLessons((current) => [lesson, ...current.filter((item) => item.id !== lesson.id)]);
    if (lesson.moduleType === "describing") {
      setSelectedDescribingLessonId(lesson.id);
    } else if (lesson.moduleType === "grammar") {
      setSelectedGrammarLessonId(lesson.id);
    } else if (lesson.moduleType === "reading") {
      setSelectedReadingLessonId(lesson.id);
    } else if (lesson.moduleType === "dialogue") {
      setSelectedDialogueLessonId(lesson.id);
    }
    const fallbackMap = lesson.moduleType === "grammar" ? selectedGrammarCurriculumMap : lesson.moduleType === "describing"
      ? selectedDescribingCurriculumMap
      : lesson.moduleType === "dialogue"
        ? selectedDialogueCurriculumMap
        : lesson.lessonKind === "kana"
          ? selectedKanaCurriculumMap ?? selectedAdminCurriculumMap
          : selectedStandardReadingCurriculumMap ?? selectedAdminCurriculumMap;
    const targetCurriculumId = placement?.curriculumId ?? fallbackMap?.curriculum.id;
    if (targetCurriculumId) {
      setCurriculumMaps((current) =>
        current.map((map) => {
          if (map.curriculum.id !== targetCurriculumId) return map;
          const exists = map.lessons.some((item) => item.lessonId === lesson.id);
          if (exists) {
            return {
              ...map,
              lessons: map.lessons.map((item) => item.lessonId === lesson.id ? { ...item, lesson } : item)
            };
          }
          const newItem: CurriculumLesson = {
            id: `local-${map.curriculum.id}-${lesson.id}`,
            curriculumId: map.curriculum.id,
            lessonId: lesson.id,
            categoryTitle: placement?.categoryTitle ?? "Core Lessons",
            sortOrder: placement?.sortOrder ?? map.lessons.length + 1,
            requiredAudioScopes: lesson.moduleType === "dialogue" ? ["full", "sentence"] : ["full", "paragraph", "sentence"],
            completionPercent: 0,
            completedAt: null,
            lesson
          };
          return { ...map, lessons: [...map.lessons, newItem] };
        })
      );
    }
    if (options.openReading) {
      setActiveView("reading");
      setReadingScreen("material");
    }
    if (options.openDescribing) {
      setActiveView("describing");
      setDescribingScreen("material");
    }
  };

  const handleCurriculumMapUpdated = (map: CurriculumMap) => {
    setCurriculumMaps((current) =>
      current.some((item) => item.curriculum.id === map.curriculum.id)
        ? current.map((item) => item.curriculum.id === map.curriculum.id ? mergeCurriculumMapPreservingProgress(item, map) : item)
        : [...current, map]
    );
    if (map.curriculum.moduleType === "reading" && !isKanaCurriculumMap(map) && !isBasicsCurriculumMap(map)) setSelectedReadingCurriculumId(map.curriculum.id);
    if (map.curriculum.moduleType === "describing") setSelectedDescribingCurriculumId(map.curriculum.id);
    if (map.curriculum.moduleType === "grammar") setSelectedGrammarCurriculumId(map.curriculum.id);
    if (map.curriculum.moduleType === "dialogue") setSelectedDialogueCurriculumId(map.curriculum.id);
    const updatedLessons = map.lessons.map((item) => item.lesson);
    setLessons((current) => dedupeLessons([
      ...updatedLessons,
      ...current.filter((lesson) => !updatedLessons.some((updatedLesson) => updatedLesson.id === lesson.id))
    ]));
  };

  const handleCurriculumMapCreated = (map: CurriculumMap) => {
    setCurriculumMaps((current) => [map, ...current.filter((item) => item.curriculum.id !== map.curriculum.id)]);
    setSelectedAdminCurriculumId(map.curriculum.id);
    if (map.curriculum.moduleType === "reading" && !isKanaCurriculumMap(map) && !isBasicsCurriculumMap(map)) setSelectedReadingCurriculumId(map.curriculum.id);
    if (map.curriculum.moduleType === "describing") setSelectedDescribingCurriculumId(map.curriculum.id);
    if (map.curriculum.moduleType === "grammar") setSelectedGrammarCurriculumId(map.curriculum.id);
    if (map.curriculum.moduleType === "dialogue") setSelectedDialogueCurriculumId(map.curriculum.id);
  };

  const handleLessonUpdated = (lesson: Lesson) => {
    setLessons((current) => [lesson, ...current.filter((item) => item.id !== lesson.id)]);
    setCurriculumMaps((current) =>
      current.map((map) => ({
        ...map,
        lessons: map.lessons.map((item) => item.lessonId === lesson.id ? { ...item, lesson } : item)
      }))
    );
  };

  useEffect(() => {
    if (activeView !== "reading" || readingScreen !== "material" || !selectedReadingLesson) return;
    if (!needsLessonDetails(selectedReadingLesson)) return;
    if (hydratedReadingLessonIdsRef.current.has(selectedReadingLesson.id)) return;

    hydratedReadingLessonIdsRef.current.add(selectedReadingLesson.id);
    api.getLesson(selectedReadingLesson.id)
      .then(handleLessonUpdated)
      .catch((error) => {
        console.warn(error instanceof Error ? error.message : "Could not load Reading lesson details.");
      });
  }, [activeView, api, readingScreen, selectedReadingLesson]);

  useEffect(() => {
    if (activeView !== "describing" || describingScreen !== "material" || !selectedDescribingLesson) return;
    if (!needsLessonDetails(selectedDescribingLesson)) return;
    if (hydratedDescribingLessonIdsRef.current.has(selectedDescribingLesson.id)) return;

    hydratedDescribingLessonIdsRef.current.add(selectedDescribingLesson.id);
    api.getLesson(selectedDescribingLesson.id)
      .then(handleLessonUpdated)
      .catch((error) => {
        console.warn(error instanceof Error ? error.message : "Could not load Describing lesson details.");
      });
  }, [activeView, api, describingScreen, selectedDescribingLesson]);

  useEffect(() => {
    if (activeView !== "grammar" || grammarScreen !== "material" || !selectedGrammarLesson) return;
    if (!needsLessonDetails(selectedGrammarLesson)) return;
    if (hydratedGrammarLessonIdsRef.current.has(selectedGrammarLesson.id)) return;

    hydratedGrammarLessonIdsRef.current.add(selectedGrammarLesson.id);
    api.getLesson(selectedGrammarLesson.id)
      .then(handleLessonUpdated)
      .catch((error) => {
        console.warn(error instanceof Error ? error.message : "Could not load Grammar lesson details.");
      });
  }, [activeView, api, grammarScreen, selectedGrammarLesson]);

  useEffect(() => {
    if (activeView !== "dialogue" || dialogueScreen !== "material" || !selectedDialogueLesson) return;
    if (!needsLessonDetails(selectedDialogueLesson) && selectedDialogueLesson.dialogueSpeakers.length === 2) return;
    if (hydratedDialogueLessonIdsRef.current.has(selectedDialogueLesson.id)) return;

    hydratedDialogueLessonIdsRef.current.add(selectedDialogueLesson.id);
    api.getLesson(selectedDialogueLesson.id)
      .then(handleLessonUpdated)
      .catch((error) => {
        console.warn(error instanceof Error ? error.message : "Could not load Dialogue lesson details.");
      });
  }, [activeView, api, dialogueScreen, selectedDialogueLesson]);

  const handleCurriculumDeleted = (curriculumId: string) => {
    const nextMaps = curriculumMaps.filter((map) => map.curriculum.id !== curriculumId);
    setCurriculumMaps(nextMaps);
    if (selectedAdminCurriculumId === curriculumId) setSelectedAdminCurriculumId(nextMaps[0]?.curriculum.id ?? "");
    if (selectedReadingCurriculumId === curriculumId) {
      setSelectedReadingCurriculumId(nextMaps.find((map) => map.curriculum.moduleType === "reading" && !isKanaCurriculumMap(map) && !isBasicsCurriculumMap(map))?.curriculum.id ?? "");
    }
    if (selectedDescribingCurriculumId === curriculumId) {
      setSelectedDescribingCurriculumId(nextMaps.find((map) => map.curriculum.moduleType === "describing")?.curriculum.id ?? "");
    }
    if (selectedGrammarCurriculumId === curriculumId) {
      setSelectedGrammarCurriculumId(nextMaps.find((map) => map.curriculum.moduleType === "grammar")?.curriculum.id ?? "");
    }
    if (selectedDialogueCurriculumId === curriculumId) {
      setSelectedDialogueCurriculumId(nextMaps.find((map) => map.curriculum.moduleType === "dialogue")?.curriculum.id ?? "");
    }
  };

  const handleLessonDeleted = (lessonId: string) => {
    const deletedLesson = lessons.find((lesson) => lesson.id === lessonId) ?? null;
    const nextLessons = lessons.filter((lesson) => lesson.id !== lessonId);
    setLessons(nextLessons);
    setCurriculumMaps((current) =>
      current.map((map) => ({
        ...map,
        lessons: map.lessons.filter((item) => item.lessonId !== lessonId)
      }))
    );
    if (deletedLesson?.moduleType === "describing" && selectedDescribingLessonId === lessonId) {
      setSelectedDescribingLessonId(nextLessons.find((lesson) => lesson.moduleType === "describing")?.id ?? "");
      setDescribingScreen("chooser");
    }
    if (deletedLesson?.moduleType === "grammar" && selectedGrammarLessonId === lessonId) {
      setSelectedGrammarLessonId(nextLessons.find((lesson) => lesson.moduleType === "grammar")?.id ?? "");
      setGrammarScreen("chooser");
    setBasicsScreen("chooser");
    }
    if ((!deletedLesson || deletedLesson.moduleType === "reading") && selectedReadingLessonId === lessonId) {
      setSelectedReadingLessonId(nextLessons.find((lesson) => lesson.moduleType === "reading")?.id ?? "");
      setReadingScreen("chooser");
    }
    if (deletedLesson?.moduleType === "dialogue" && selectedDialogueLessonId === lessonId) {
      setSelectedDialogueLessonId(nextLessons.find((lesson) => lesson.moduleType === "dialogue")?.id ?? "");
      setDialogueScreen("chooser");
    }
  };

  const handleLessonFinished = async (lessonId: string, finished: boolean) => {
    const progress = await api.setLessonFinished(lessonId, finished);
    if (progress.userId !== selectedLearnerId) {
      throw new Error("Finished status was returned for a different learner. Refresh and try again.");
    }
    setCurriculumMaps((current) =>
      current.map((map) => ({
        ...map,
        lessons: map.lessons.map((item) => item.lessonId === lessonId
          ? { ...item, completionPercent: progress.completionPercent, completedAt: progress.completedAt }
          : item)
      }))
    );
  };

  const openLessonInCurriculumAdmin = (lessonId: string) => {
    setAdminEditLessonId(lessonId);
    setActiveView("curriculum");
  };

  const switchLearner = async (learnerId: string, learnerPool = learners) => {
    if (!learnerPool.some((item) => item.id === learnerId)) return;
    if (learnerId === selectedLearnerId && learningDataState === "ready") return;
    setReadingScreen("chooser");
    setDescribingScreen("chooser");
    setGrammarScreen("chooser");
    setBasicsScreen("chooser");
    setDialogueScreen("chooser");
    try {
      await loadLearningData(learnerId);
    } catch (error) {
      console.warn(error instanceof Error ? error.message : "Learner switch failed.");
    }
  };

  useEffect(() => {
    if (activeView !== "basics" || basicsScreen !== "material" || !selectedBasicsLesson || !needsLessonDetails(selectedBasicsLesson)) return;
    let cancelled = false;
    api.getLesson(selectedBasicsLesson.id).then(lesson => { if (!cancelled) handleLessonUpdated(lesson); }).catch(error => console.warn(error));
    return () => { cancelled = true; };
  }, [activeView, api, basicsScreen, selectedBasicsLesson]);
  const sendEvents = (events: unknown[]) => {
    if (accessState !== "unlocked") return;
    api.sendEvents(events).catch(() => undefined);
  };

  const updateFuriganaPreference = (enabled: boolean) => {
    if (!activeCourse || activeCourse.languageCode !== "ja") return;
    setFuriganaEnabled(enabled);
    void api.updateLearningPreference(activeCourse.id, enabled).catch((error) => {
      setFuriganaEnabled(!enabled);
      window.alert(error instanceof Error ? error.message : "Could not save the furigana preference.");
    });
  };

  if (accessState !== "unlocked") {
    return (
      <PinGate
        pinInput={pinInput}
        message={accessState === "checking" ? "Checking access..." : pinMessage}
        checking={accessState === "checking"}
        onPinInput={setPinInput}
        onSubmit={unlockWithPin}
      />
    );
  }

  return (
    <SentenceTranslationProvider api={api} enabled={koreanTranslationEnabled} lesson={translationLesson?.status === "ready" ? translationLesson : null}>
    <div className={`app-shell ${isJapaneseCourse && !isComputerSelected ? "japanese-mode" : ""}`}>
      <aside className="sidebar">
        <div className="brand logo-brand" aria-label="Jireh-Lingo">
          <span className="logo-green">Jireh-</span><span className="logo-blue">Lingo</span>
          <svg className="logo-mark" viewBox="0 0 32 32" aria-hidden="true">
            <path fill="currentColor" d="M15.8 2.8l3.2 7 7.5.9-5.5 5.2 1.5 7.4-6.7-3.7-6.6 3.7 1.4-7.4-5.4-5.2 7.5-.9z" />
            <circle cx="24.5" cy="7.2" r="3.4" fill="#1cb0f6" />
          </svg>
        </div>

        <div className="account-box learner-box">
          <div className="learner-select-row">
            <select
              value={selectedLearnerId}
              disabled={learningDataState === "loading"}
              onChange={(event) => void switchLearner(event.target.value)}
              aria-label="Current learner"
            >
              {learners.map((learner) => (
                <option key={learner.id} value={learner.id}>{learner.displayName}</option>
              ))}
            </select>
          </div>
        </div>

        <SubjectSwitch
          courses={learningLanguageCourses(courses)}
          activeCourseId={activeCourse?.id}
          computerSelected={isComputerSelected}
          onSelectCourse={(course) => {
                  playUiClick();
                  setActiveCourseId(course.id);
                  if (isComputerSelected) setActiveView("reading");
                  if (course.languageCode === "ja") { setJapaneseReadingSection("kana"); setActiveView("reading"); }
                  setReadingScreen("chooser");
                  setDescribingScreen("chooser");
                  setGrammarScreen("chooser");
    setBasicsScreen("chooser");
                  setDialogueScreen("chooser");
          }}
          onSelectComputer={() => { playUiClick(); setActiveView("computer"); }}
        />

        <nav className="screen-nav" aria-label="Main screens">
          {isComputerSelected ? <button className="active computer-navigation" type="button" aria-current="page"><Monitor size={22} /> <strong>Programming Thinking</strong></button> : <>
          {activeCourse?.languageCode === "ja" ? (
            <button
              className={kanaNavActive ? "active" : ""}
              type="button"
              onClick={() => {
                playUiClick();
                setActiveView("reading");
                setJapaneseReadingSection("kana");
                setSelectedKanaItemId(null);
                setReadingScreen("chooser");
              }}
            >
              <span className="kana-nav-glyph" lang="ja">あ</span> <strong lang="ja">かな</strong>
            </button>
          ) : null}
          {isJapaneseCourse ? <button className={activeView === "reading" && japaneseReadingSection === "expressions" ? "active" : ""} onClick={() => {
            playUiClick(); setActiveView("reading"); setJapaneseReadingSection("expressions"); setSelectedReadingCurriculumId(EXPRESSIONS_CURRICULUM_ID);
            setSelectedReadingLessonId(activeCourseMaps.find(map => map.curriculum.id === EXPRESSIONS_CURRICULUM_ID)?.lessons[0]?.lessonId ?? ""); setReadingScreen("chooser");
          }}><span className="kana-nav-glyph">💬</span><strong>표현</strong></button> : null}
          {activeCourse?.languageCode !== "ja" ? <>
          <button
            className={activeView === "reading" && !kanaNavActive ? "active" : ""}
            type="button"
            onClick={() => {
              playUiClick();
              setActiveView("reading");
              setReadingScreen("chooser");
            }}
          >
            <BookOpen size={22} /> <strong>Reading</strong>
          </button>
          <button
            className={activeView === "describing" ? "active" : ""}
            type="button"
            onClick={() => {
              playUiClick();
              setActiveView("describing");
              setDescribingScreen("chooser");
            }}
          >
            <SearchCheck size={22} /> <strong>Describing</strong>
          </button>
          <button
            className={activeView === "dialogue" ? "active" : ""}
            type="button"
            onClick={() => {
              playUiClick();
              setActiveView("dialogue");
              setDialogueScreen("chooser");
            }}
          >
            <MessageSquareText size={22} /> <strong>Dialogue</strong>
          </button>
          </> : null}
          {activeCourse?.languageCode === "en" ? <button className={activeView === "grammar" ? "active" : ""} type="button" onClick={() => { playUiClick(); setActiveView("grammar"); setGrammarScreen("chooser"); }}><BookOpen size={22} /><strong>Grammar</strong></button> : null}
          {activeCourse?.languageCode === "en" ? <button className={activeView === "basics" ? "active" : ""} type="button" onClick={() => { playUiClick(); setActiveView("basics"); setBasicsScreen("chooser"); }}><BookOpen size={22} /><strong>Basics</strong></button> : null}
          </>}
        </nav>

        {!isComputerSelected ? <nav className="screen-nav admin-screen-nav" aria-label="Admin screens">
          <button className={activeView === "ai-generation" ? "active" : ""} type="button" onClick={() => {
            playUiClick();
            setActiveView("ai-generation");
          }}>
            <Sparkles size={22} /> <strong>AI Generation</strong>
          </button>
          <button className={activeView === "curriculum" ? "active" : ""} type="button" onClick={() => {
            playUiClick();
            setAdminEditLessonId(null);
            setActiveView("curriculum");
          }}>
            <Map size={22} /> <strong>Curriculum Admin</strong>
          </button>
          <button className={activeView === "analytics" ? "active" : ""} type="button" onClick={() => {
            playUiClick();
            setActiveView("analytics");
          }}>
            <BarChart3 size={22} /> <strong>Admin Analytics</strong>
          </button>
        </nav> : null}
        {activeCourse?.languageCode === "en" ? <div className="sidebar-translation">
          <label className="furigana-toggle korean-translation-toggle">
            <span><strong>Korean translation</strong><small>Hover over a sentence to view</small></span>
            <input type="checkbox" role="switch" aria-label="Korean translation" checked={koreanTranslationEnabled} onChange={(event) => {
              const enabled = event.target.checked;
              setKoreanTranslationEnabled(enabled);
              try { localStorage.setItem(KOREAN_TRANSLATION_STORAGE_KEY, enabled ? "on" : "off"); } catch { /* Keep the control usable when browser storage is unavailable. */ }
            }} />
            <span className="translation-toggle-state" aria-hidden="true">{koreanTranslationEnabled ? "On" : "Off"}</span>
            <span className="toggle-track" aria-hidden="true"><span /></span>
          </label>
        </div> : null}
        {isJapaneseCourse ? <div className="sidebar-translation">
          <label className="furigana-toggle korean-translation-toggle">
            <span><strong lang="ja">ふりがな</strong><small>읽는 법 표시</small></span>
            <input type="checkbox" role="switch" aria-label="후리가나 읽는 법 표시" checked={furiganaEnabled} onChange={(event) => updateFuriganaPreference(event.target.checked)} />
            <span className="translation-toggle-state" aria-hidden="true">{furiganaEnabled ? "On" : "Off"}</span>
            <span className="toggle-track" aria-hidden="true"><span /></span>
          </label>
        </div> : null}
      </aside>

      <main className="main">
        {learningDataState === "error" ? <p role="alert">{learningDataMessage}</p> : null}
        <Suspense fallback={<p role="status">Loading screen...</p>}>
        {learningDataState === "ready" ? <>
        {activeView === "computer" ? <ComputerView key={selectedLearnerId} learnerId={selectedLearnerId} /> : null}
        {activeView === "reading" ? (
          <ReadingView
            key={selectedLearnerId}
            api={api}
            isJapanese={activeCourse?.languageCode === "ja"}
            showWritingPractice={activeCourse?.languageCode === "en"}
            lesson={selectedReadingLesson}
            curriculumMap={selectedReadingCurriculumMap}
            availableCurricula={isKanaSection ? kanaMaps : readingMaps}
            selectedLessonId={selectedReadingLessonId}
            curriculumTitle={selectedReadingCurriculumMap?.curriculum.title ?? "Reading Curriculum"}
            screen={readingScreen}
            onEvents={sendEvents}
            onSelectLesson={(lessonId, kanaItemId, suppressUiClick) => {
              if (!suppressUiClick) playUiClick();
              setSelectedReadingLessonId(lessonId);
              setSelectedKanaItemId(kanaItemId ?? null);
              setReadingScreen("material");
            }}
            onToggleLessonFinished={handleLessonFinished}
            onBackToChooser={() => setReadingScreen("chooser")}
            onOpenIngest={() => openLessonInCurriculumAdmin(selectedReadingLessonId)}
            showFurigana={activeCourse?.languageCode === "ja" && furiganaEnabled}
            japaneseSection={japaneseReadingSection}
            onJapaneseSectionChange={setJapaneseReadingSection}
            selectedKanaItemId={selectedKanaItemId}
            onLessonUpdated={handleLessonUpdated}
            onSelectCurriculum={(curriculumId) => {
              setSelectedReadingCurriculumId(curriculumId);
              const nextMap = readingMaps.find((map) => map.curriculum.id === curriculumId);
              setSelectedReadingLessonId(nextMap?.lessons[0]?.lessonId ?? "");
            }}
          />
        ) : null}
        {activeView === "basics" && activeCourse?.languageCode === "en" ? <SentenceLessonView mode="basics" lesson={selectedBasicsLesson} curriculumMap={basicsMap} screen={basicsScreen} onEvents={sendEvents} onSelectLesson={(id) => { playUiClick(); setSelectedBasicsLessonId(id); setBasicsScreen("material"); }} onToggleLessonFinished={handleLessonFinished} onBackToChooser={() => setBasicsScreen("chooser")} onManage={() => { playUiClick(); setSelectedAdminCurriculumId(BASICS_CURRICULUM_ID); setActiveView("curriculum"); }} /> : null}
        {activeView === "grammar" && activeCourse?.languageCode === "en" ? <SentenceLessonView mode="grammar" lesson={selectedGrammarLesson} curriculumMap={selectedGrammarCurriculumMap} screen={grammarScreen} onEvents={sendEvents} onSelectLesson={(id) => { playUiClick(); setSelectedGrammarLessonId(id); setGrammarScreen("material"); }} onToggleLessonFinished={handleLessonFinished} onBackToChooser={() => setGrammarScreen("chooser")} /> : null}
        {activeView === "describing" ? (
          <DescribingView
            api={api}
            lesson={selectedDescribingLesson}
            curriculumMap={selectedDescribingCurriculumMap}
            selectedLessonId={selectedDescribingLessonId}
            curriculumTitle={selectedDescribingCurriculumMap?.curriculum.title ?? "Describing Curriculum"}
            screen={describingScreen}
            onEvents={sendEvents}
            onSelectLesson={(lessonId) => {
              playUiClick();
              setSelectedDescribingLessonId(lessonId);
              setDescribingScreen("material");
            }}
            onToggleLessonFinished={handleLessonFinished}
            onBackToChooser={() => setDescribingScreen("chooser")}
            onOpenDescriptions={() => openLessonInCurriculumAdmin(selectedDescribingLessonId)}
            showFurigana={activeCourse?.languageCode === "ja" && furiganaEnabled}
            showWritingPractice={activeCourse?.languageCode === "en"}
          />
        ) : null}
        {activeView === "dialogue" ? (
          <DialogueView
            api={api}
            lesson={selectedDialogueLesson}
            curriculumMap={selectedDialogueCurriculumMap}
            selectedLessonId={selectedDialogueLessonId}
            curriculumTitle={selectedDialogueCurriculumMap?.curriculum.title ?? "Dialogue Curriculum"}
            screen={dialogueScreen}
            onEvents={sendEvents}
            onSelectLesson={(lessonId) => {
              playUiClick();
              setSelectedDialogueLessonId(lessonId);
              setDialogueScreen("material");
            }}
            onToggleLessonFinished={handleLessonFinished}
            onBackToChooser={() => setDialogueScreen("chooser")}
            showFurigana={activeCourse?.languageCode === "ja" && furiganaEnabled}
            showWritingPractice={activeCourse?.languageCode === "en"}
          />
        ) : null}
        {activeView === "curriculum" ? (
          <CurriculumAdminView
            key={activeCourse?.id}
            api={api}
            courses={courses}
            lessons={lessons}
            curriculumMaps={curriculumMaps}
            selectedCurriculumId={selectedAdminCurriculumId}
            onCurriculumMapCreated={handleCurriculumMapCreated}
            onCurriculumMapUpdated={handleCurriculumMapUpdated}
            onCurriculumDeleted={handleCurriculumDeleted}
            onSelectCurriculum={setSelectedAdminCurriculumId}
            onLessonDeleted={handleLessonDeleted}
            onLessonUpdated={handleLessonUpdated}
            initialLessonId={adminEditLessonId}
            onLessonCreated={handleLessonCreated}
            activeCourseId={activeCourse?.id ?? ""}
          />
        ) : null}
        {activeView === "ai-generation" ? (
          <AiGenerationView
            api={api}
            curriculumMaps={curriculumMaps}
            initialCurriculumId={selectedAdminCurriculumId}
            initialCategoryTitle="Core Lessons"
            onLessonAvailable={(lesson, map) => {
              handleLessonUpdated(lesson);
              handleCurriculumMapUpdated(map);
              setSelectedAdminCurriculumId(map.curriculum.id);
            }}
            onOpenLesson={(lesson, map) => {
              handleLessonUpdated(lesson);
              handleCurriculumMapUpdated(map);
              setSelectedAdminCurriculumId(map.curriculum.id);
              setAdminEditLessonId(lesson.id);
              setActiveView("curriculum");
            }}
            activeCourseId={activeCourse?.id ?? ""}
            activeCourseLanguageCode={activeCourse?.languageCode ?? null}
          />
        ) : null}
        {activeView === "analytics" ? <AnalyticsView analytics={analytics} /> : null}
        </> : learningDataState === "loading" ? <p role="status">Loading lessons...</p> : null}
        </Suspense>
      </main>
    </div>
    </SentenceTranslationProvider>
  );
}

function playUiClickSound(contextRef: MutableRefObject<AudioContext | null>): void {
  try {
    const AudioContextConstructor = window.AudioContext
      ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!AudioContextConstructor) return;
    const context = contextRef.current ?? new AudioContextConstructor();
    contextRef.current = context;
    if (context.state === "suspended") void context.resume();

    const now = context.currentTime;
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(920, now);
    oscillator.frequency.exponentialRampToValueAtTime(520, now + 0.045);
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.08, now + 0.006);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.055);
    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(now);
    oscillator.stop(now + 0.06);
  } catch {
    // Sound feedback is optional; navigation should never fail because audio is blocked.
  }
}

function dedupeLessons(lessonList: Lesson[]): Lesson[] {
  const seen = new Set<string>();
  const result: Lesson[] = [];
  for (const lesson of lessonList) {
    if (seen.has(lesson.id)) continue;
    seen.add(lesson.id);
    result.push(lesson);
  }
  return result;
}

export function addUnplacedLessonsToCurriculumMaps(curriculumMaps: CurriculumMap[], lessonList: Lesson[]): CurriculumMap[] {
  const placedLessonIds = new Set(curriculumMaps.flatMap((map) => map.lessons.map((item) => item.lessonId)));
  const firstCurriculumIdByType = new globalThis.Map<string, string>();
  for (const map of curriculumMaps) {
    const type = `${map.curriculum.courseId}:${isKanaCurriculumMap(map) ? "kana" : map.curriculum.moduleType}`;
    if (!firstCurriculumIdByType.has(type)) {
      firstCurriculumIdByType.set(type, map.curriculum.id);
    }
  }

  return curriculumMaps.map((map) => {
    const type = `${map.curriculum.courseId}:${isKanaCurriculumMap(map) ? "kana" : map.curriculum.moduleType}`;
    if (firstCurriculumIdByType.get(type) !== map.curriculum.id) return map;
    const unplacedLessons = lessonList.filter((lesson) =>
      `${lesson.courseId}:${lesson.lessonKind === "kana" ? "kana" : lesson.moduleType}` === type
      && lesson.status !== "archived"
      && !placedLessonIds.has(lesson.id)
    );
    if (unplacedLessons.length === 0) return map;
    const startOrder = Math.max(0, ...map.lessons.map((item) => item.sortOrder));
    const unplacedItems = unplacedLessons.map((lesson, index): CurriculumLesson => ({
      id: `local-unplaced-${map.curriculum.id}-${lesson.id}`,
      curriculumId: map.curriculum.id,
      lessonId: lesson.id,
      categoryTitle: "Core Lessons",
      sortOrder: startOrder + index + 1,
      requiredAudioScopes: lesson.lessonKind === "kana"
        ? lesson.kanaItems.map((item) => `kana:${item.id}` as const)
        : ["full", "paragraph", "sentence"],
      completionPercent: 0,
      completedAt: null,
      lesson
    }));
    return { ...map, lessons: [...map.lessons, ...unplacedItems] };
  });
}

export function applyLessonProgressToCurriculumMaps(curriculumMaps: CurriculumMap[], progressList: LessonProgress[]): CurriculumMap[] {
  const progressByLessonId = new globalThis.Map(progressList.map((progress) => [progress.lessonId, progress]));
  return curriculumMaps.map((map) => ({
    ...map,
    lessons: map.lessons.map((item) => {
      const progress = progressByLessonId.get(item.lessonId);
      return progress
        ? { ...item, completionPercent: progress.completionPercent, completedAt: progress.completedAt }
        : item;
    })
  }));
}

function keepSelectedLesson(current: string, lessonList: Lesson[], moduleType: Lesson["moduleType"]): string {
  const moduleLessons = lessonList.filter((lesson) => lesson.moduleType === moduleType);
  return moduleLessons.some((lesson) => lesson.id === current) ? current : moduleLessons[0]?.id ?? "";
}

function keepSelectedLessonForCourse(
  current: string,
  lessonList: Lesson[],
  moduleType: Lesson["moduleType"],
  courseId: string
): string {
  const moduleLessons = lessonList.filter((lesson) => lesson.moduleType === moduleType && lesson.courseId === courseId);
  return moduleLessons.some((lesson) => lesson.id === current) ? current : moduleLessons[0]?.id ?? "";
}

export function needsLessonDetails(lesson: Lesson | null): boolean {
  if (!lesson) return false;
  return lesson.sourceText.trim().length === 0 || lesson.segments.length === 0 || lesson.assets.length === 0;
}

function readStoredLearnerId(): string {
  try {
    return window.localStorage.getItem(LEARNER_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function storeLearnerId(learnerId: string): void {
  try {
    window.localStorage.setItem(LEARNER_STORAGE_KEY, learnerId);
  } catch {
    // The app still works when the browser blocks local storage.
  }
}

function readStoredCourseId(): string {
  try {
    return window.localStorage.getItem(COURSE_STORAGE_KEY) ?? "";
  } catch {
    return "";
  }
}

function storeCourseId(courseId: string): void {
  try {
    window.localStorage.setItem(COURSE_STORAGE_KEY, courseId);
  } catch {
    // Course switching still works when the browser blocks local storage.
  }
}

interface PinGateProps {
  pinInput: string;
  message: string;
  checking: boolean;
  onPinInput: (value: string) => void;
  onSubmit: () => void;
}

function PinGate({ pinInput, message, checking, onPinInput, onSubmit }: PinGateProps) {
  return (
    <main className="pin-gate">
      <section className="pin-panel">
        <div className="brand">
          <div className="brand-mark">
            <LockKeyhole size={22} aria-hidden="true" />
          </div>
          <div>
            <h1>Jireh-Lingo</h1>
            <p>Enter the app PIN to continue.</p>
          </div>
        </div>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            onSubmit();
          }}
        >
          <label>
            <span>PIN</span>
            <div className="pin-row">
              <input
                autoFocus
                inputMode="numeric"
                type="password"
                value={pinInput}
                onChange={(event) => onPinInput(event.target.value)}
              />
              <button className="primary-button" disabled={checking} type="submit">
                <KeyRound size={17} aria-hidden="true" /> Unlock
              </button>
            </div>
          </label>
        </form>
        <p className="status-line">{message}</p>
      </section>
    </main>
  );
}

